import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/* Utilities */
import { trans, getCloudImageUrl, getLocaleUrl, currency } from 'js/utilities';

/* Symbols */
import buttonIcon from 'symbols/arrow.svg';

/* Constants */
import { fadeInOut } from 'js/constants/animations';

/* Common */
import {Button} from 'js/components/atoms';

export default function SearchResults({ results, resultsUrl = '', locale }) {
    const { vehicles, pages, actions, news, products } = results || {};
    const totalResults = vehicles?.count || 0 + pages?.count || 0 + actions?.count || 0 + news?.count || 0 + products?.count || 0;
    const productItems = products?.items?.slice(0, 4) || [];
    return (
        <AnimatePresence initial={false} exitBeforeEnter>
            {totalResults > 0 && (
                <motion.div key="results" className="search__overlay-results" {...fadeInOut}>
                    <div className="row gutters-y-4 row--bordered flex-column mb-6">
                        <AnimatePresence initial={false} exitBeforeEnter>
                            {vehicles?.count > 0 && (
                                <motion.div key="vehicles" className="col-12" {...fadeInOut}>
                                    <p className="font-weight-extra-bold text-md mb-2 mb-lg-4 text-base">{trans('header.search.vehicles')} ({vehicles.count})</p>
                                    <div className="row gutters-2">
                                        <AnimatePresence initial={false} exitBeforeEnter>
                                            {vehicles?.items?.map((vehicle, vehicleIndex) => (
                                                <motion.div key={vehicle?.id || vehicleIndex} className="col-12" {...fadeInOut}>
                                                    <a href={getLocaleUrl(vehicle.url, locale)} className="text-decoration-none">
                                                        <div className="d-flex align-items-center gap-4">
                                                            {vehicle?.images?.length > 0 ?
                                                                (
                                                                    <img className="rounded" src={getCloudImageUrl(vehicle?.images?.[0]?.path, { w: 110, h: 70, func: 'crop' })} alt={`${vehicle?.brand} ${vehicle?.model} ${vehicle?.type}`} width="110" height="70"/>
                                                                )
                                                                :
                                                                (
                                                                    <div className="rounded bg-light mr-3" style={{ width: '110px', height: '70' }}></div>
                                                                )
                                                            }
                                                            <div className="overflow-hidden">
                                                                <p className="mb-0 text-decoration-none font-weight-extra-bold text--16 text-base text-truncate">{vehicle?.brand} {vehicle?.model}</p>
                                                                <p className="text--14 text-truncate">{vehicle?.type}</p>
                                                            </div>
                                                        </div>
                                                    </a>
                                                </motion.div>
                                            ))}
                                        </AnimatePresence>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                        <AnimatePresence initial={false} exitBeforeEnter>
                            {products?.count > 0 && (
                                <motion.div key="products" className="col-12" {...fadeInOut}>
                                    <p className="font-weight-extra-bold text-md mb-2 mb-lg-4 text-base">{trans('header.search.products')} ({products.count})</p>
                                    <div className="row gutters-2">
                                        <AnimatePresence initial={false} exitBeforeEnter>
                                            {productItems.map((product, productIndex) => (
                                                <motion.div key={product?.id || productIndex} className="col-12" {...fadeInOut}>
                                                    <a href={product.url} className="text-decoration-none">
                                                        <div className="d-flex align-items-center gap-4">
                                                            {product?.image ?
                                                                (
                                                                    <img className="rounded" src={getCloudImageUrl(product.image, { w: 110, h: 70, func: 'crop' })} alt={product.name} width="110" height="70"/>
                                                                )
                                                                :
                                                                (
                                                                    <div className="rounded bg-light mr-3" style={{ width: '110px', height: '70px' }}></div>
                                                                )
                                                            }
                                                            <div className="overflow-hidden">
                                                                <p className="mb-0 text-decoration-none font-weight-extra-bold text--16 text-base text-truncate">{product?.name}</p>
                                                                {product?.price > 0 && (
                                                                    <p className="text--14 text-truncate mb-0">{currency(product.price)}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </a>
                                                </motion.div>
                                            ))}
                                        </AnimatePresence>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                        <AnimatePresence initial={false} exitBeforeEnter>
                            {pages?.count > 0 && (
                                <motion.div className="col-12" key="pages" {...fadeInOut}>
                                    <p className="font-weight-extra-bold text-md mb-1 text-base">{trans('header.search.pages')} ({pages.count})</p>
                                    <AnimatePresence initial={false} exitBeforeEnter>
                                        {pages?.items?.map((page, pageIndex) => (
                                            <motion.a key={page?.id || pageIndex} className="text-decoration-none font-weight-light text--14" href={page.url} title={page.title} {...fadeInOut}><p className="search-result-item text-truncate">{page.title}</p></motion.a>
                                        ))}
                                    </AnimatePresence>
                                </motion.div>
                            )}
                        </AnimatePresence>
                        <AnimatePresence initial={false} exitBeforeEnter>
                            {actions?.count > 0 && (
                                <motion.div className="col-12" key="actions" {...fadeInOut}>
                                    <p className="font-weight-extra-bold text-md mb-1 text-base">{trans('header.search.actions')} ({actions.count})</p>
                                    <AnimatePresence initial={false} exitBeforeEnter>
                                        {actions?.items?.map((action, actionIndex) => (
                                            <motion.a key={action?.id || actionIndex} className="text-decoration-none font-weight-light text--14" href={action.url} title={action.title} {...fadeInOut}><p className="search-result-item text-truncate">{action.title}</p></motion.a>
                                        ))}
                                    </AnimatePresence>
                                </motion.div>
                            )}
                        </AnimatePresence>
                        <AnimatePresence initial={false} exitBeforeEnter>
                            {news?.count > 0 && (
                                <motion.div className="col-12" key="news" {...fadeInOut}>
                                    <p className="font-weight-extra-bold text-md mb-1 text-base">{trans('header.search.news')} ({news.count})</p>
                                    <AnimatePresence initial={false} exitBeforeEnter>
                                        {news?.items?.map((news, newsIndex) => (
                                            <motion.a key={news?.id || newsIndex} className="text-decoration-none font-weight-light text--14" href={news.url} title={news.title} {...fadeInOut}><p className="search-result-item text-truncate">{news.title}</p></motion.a>
                                        ))}
                                    </AnimatePresence>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    <Button
                        text={trans('header.search.show_all_results')}
                        theme="primary"
                        href={resultsUrl}
                        icon={buttonIcon}
                    />
                </motion.div>
            )}
            {totalResults === 0 && results !== null && (<p className="text--14 font-weight-extra-bold">{trans('search.no_results')}</p>)}
        </AnimatePresence>
    );
}
